import React, {useState, memo} from "react";
import PropTypes from "prop-types";
import {IconButton, Menu, MenuItem, ListItemIcon, ListItemText} from "@mui/material";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import PrintIcon from "@mui/icons-material/Print";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import {usePermissions} from "../../context/AuthzProvider";
import {chatbotSessionToPlainText} from "../../components/chatbot/chatbotHelpers";
import {ALERT} from "../../resources/environment";
import {useRexAITrustedAdvisorStyles} from "./useRexAITrustedAdvisorStyles";

const SessionMenu = ({
  session,
  messages,
  isCurrentSession,
  onRename,
  onDelete,
  onShare,
  onTogglePin,
  onLeaveSession,
  isVisible = true,
}) => {
  const {classes, cx} = useRexAITrustedAdvisorStyles();
  const {canShareTrustedAdvisor} = usePermissions();
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const open = Boolean(anchorEl);
  const isMenuRevealed = isVisible || open;
  const hasMessages = isCurrentSession && messages.length > 0;

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    event?.stopPropagation();
    setAnchorEl(null);
    setConfirmingDelete(false);
    setConfirmingLeave(false);
  };

  const handleRename = (event) => {
    event.stopPropagation();
    handleClose();
    onRename(session.sessionId);
  };

  const handleTogglePin = async (event) => {
    event.stopPropagation();
    handleClose();
    await onTogglePin(session.sessionId, !session.pinnedAt);
  };

  const handleDelete = (event) => {
    event.stopPropagation();
    if (confirmingDelete) {
      handleClose();
      onDelete(event, session.sessionId);
    } else {
      setConfirmingDelete(true);
    }
  };

  const handleCopy = (event) => {
    event.stopPropagation();
    navigator?.clipboard?.writeText(chatbotSessionToPlainText(messages));
    ALERT.success("Session copied to clipboard");
    handleClose();
  };

  const handleCopySessionId = async (event) => {
    event.stopPropagation();
    handleClose();
    try {
      await navigator.clipboard.writeText(session.sessionId);
      ALERT.success("Session ID copied to clipboard");
    } catch {
      ALERT.error("Could not copy the session ID");
    }
  };

  const handlePrint = (event) => {
    event.stopPropagation();
    handleClose();
    setTimeout(() => {
      const oldTitle = document.title;
      document.title = session.sessionName || "Rex AI Maestro";
      window.print();
      document.title = oldTitle;
    }, 100);
  };

  const handleLeave = async (event) => {
    event.stopPropagation();
    if (!confirmingLeave) {
      setConfirmingLeave(true);
      return;
    }
    handleClose();
    await onLeaveSession(session.sessionId);
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        disableRipple
        className={cx(
          classes.sessionMenuButton,
          !isMenuRevealed && classes.sessionMenuButtonHidden,
        )}
        aria-label="Session options"
        aria-hidden={!isMenuRevealed}
        tabIndex={isMenuRevealed ? 0 : -1}
      >
        <MoreHorizIcon fontSize="small" className={classes.sessionMenuIcon} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleTogglePin}>
          <ListItemIcon>
            {session.pinnedAt ?
              <PushPinIcon fontSize="small" /> :
              <PushPinOutlinedIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{session.pinnedAt ? "Unpin session" : "Pin session"}</ListItemText>
        </MenuItem>
        {!session.shared && (
          <MenuItem onClick={handleRename}>
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Rename session</ListItemText>
          </MenuItem>
        )}
        {!session.shared && (canShareTrustedAdvisor || session.hasActiveParticipants) && (
          <MenuItem
            onClick={(event) => {
              event.stopPropagation();
              handleClose();
              onShare(session);
            }}
          >
            <ListItemIcon>
              <PersonAddAltIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {session.hasActiveParticipants ? "Manage sharing" : "Share session"}
            </ListItemText>
          </MenuItem>
        )}
        <MenuItem onClick={handleCopy} disabled={!hasMessages}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy session</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleCopySessionId}>
          <ListItemIcon>
            <FingerprintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Copy session ID</ListItemText>
        </MenuItem>
        <MenuItem onClick={handlePrint} disabled={!hasMessages}>
          <ListItemIcon>
            <PrintIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Print / Save as PDF</ListItemText>
        </MenuItem>
        {!session.shared && (
          <MenuItem
            onClick={handleDelete}
            className={confirmingDelete ? classes.menuItemError : undefined}
          >
            <ListItemIcon>
              <DeleteOutlineIcon
                fontSize="small"
                className={confirmingDelete ? classes.menuItemError : undefined}
              />
            </ListItemIcon>
            <ListItemText>
              {confirmingDelete ? "Click to confirm delete" : "Delete session"}
            </ListItemText>
          </MenuItem>
        )}
        {session.shared && (
          <MenuItem
            onClick={handleLeave}
            className={confirmingLeave ? classes.menuItemError : undefined}
          >
            <ListItemIcon>
              <ExitToAppIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>
              {confirmingLeave ? "Click to confirm leave" : "Leave shared session"}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

SessionMenu.propTypes = {
  session: PropTypes.object.isRequired,
  messages: PropTypes.array.isRequired,
  isCurrentSession: PropTypes.bool.isRequired,
  onRename: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired,
  onTogglePin: PropTypes.func.isRequired,
  onLeaveSession: PropTypes.func.isRequired,
  isVisible: PropTypes.bool,
};

export default memo(SessionMenu);
